import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft,
  TrendingUp,
  Calculator,
  Sparkles,
  CreditCard,
  Target,
  Zap,
  ChevronRight,
  Info,
  Plus,
  Minus,
  FileText
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Slider } from "../components/ui/slider";
import CreditReportAnalyzer from "../components/CreditReportAnalyzer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScoreSimulator() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [creditScore, setCreditScore] = useState("");
  const [tradelines, setTradelines] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showAnalyzer, setShowAnalyzer] = useState(false);
  const [extractedAccounts, setExtractedAccounts] = useState(null);

  useEffect(() => {
    fetchClient();
  }, [id]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API}/clients/${id}`);
      setClient(response.data);
      // Use the average of 3 bureau scores, or individual scores if available
      const equifax = response.data.score_equifax;
      const experian = response.data.score_experian;
      const transunion = response.data.score_transunion;
      
      if (equifax || experian || transunion) {
        const scores = [equifax, experian, transunion].filter(s => s);
        const avgScore = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
        setCreditScore(avgScore.toString());
      } else if (response.data.credit_score) {
        setCreditScore(response.data.credit_score.toString());
      }
    } catch (error) {
      toast.error("Failed to fetch client data");
    } finally {
      setLoading(false);
    }
  };

  const saveScore = async () => {
    if (!creditScore || isNaN(parseInt(creditScore))) {
      toast.error("Please enter a valid credit score");
      return;
    }
    
    setSaving(true);
    try {
      // Update all 3 bureau scores if they don't exist
      const updateData = { ...client };
      const score = parseInt(creditScore);
      
      if (!client.score_equifax) updateData.score_equifax = score;
      if (!client.score_experian) updateData.score_experian = score;
      if (!client.score_transunion) updateData.score_transunion = score;
      updateData.credit_score = score;
      
      await axios.put(`${API}/clients/${id}`, updateData);
      setClient(updateData);
      toast.success("Credit score saved!");
    } catch (error) {
      toast.error("Failed to save score");
    } finally {
      setSaving(false);
    }
  };

  const runSimulation = async () => {
    const currentScore = parseInt(creditScore);
    if (!currentScore) {
      toast.error("Please enter and save a credit score first");
      return;
    }
    
    // Save score first if client doesn't have one
    if (!client?.credit_score && !client?.score_equifax) {
      await saveScore();
    }
    
    setSimulating(true);
    try {
      const response = await axios.post(`${API}/clients/${id}/simulate-score?tradelines_to_add=${tradelines}`);
      setSimulation(response.data);
    } catch (error) {
      toast.error("Failed to run simulation");
    } finally {
      setSimulating(false);
    }
  };

  // Get current average score from client data
  const getCurrentScore = () => {
    if (client?.score_equifax || client?.score_experian || client?.score_transunion) {
      const scores = [client.score_equifax, client.score_experian, client.score_transunion].filter(s => s);
      return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    }
    return client?.credit_score || parseInt(creditScore) || 0;
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case "Exceptional": return "text-green-400 bg-green-500/20";
      case "Very Good": return "text-emerald-400 bg-emerald-500/20";
      case "Good": return "text-blue-400 bg-blue-500/20";
      case "Fair": return "text-amber-400 bg-amber-500/20";
      default: return "text-red-400 bg-red-500/20";
    }
  };

  const getScoreColor = (score) => {
    if (score >= 800) return "text-green-400";
    if (score >= 740) return "text-emerald-400";
    if (score >= 670) return "text-blue-400";
    if (score >= 580) return "text-amber-400";
    return "text-red-400";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="score-simulator-page" className="space-y-6 md:space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Link to={`/clients/${id}`}>
          <Button variant="ghost" size="icon" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl md:text-4xl font-bold tracking-tight">Credit Score Simulator</h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">
            {client?.name}'s projected score improvement
          </p>
        </div>
        <Button
          onClick={() => setShowAnalyzer(!showAnalyzer)}
          className={showAnalyzer ? "bg-blue-600 hover:bg-blue-700" : ""}
          data-testid="toggle-analyzer-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          {showAnalyzer ? "Hide Analyzer" : "Analyze Credit Report"}
        </Button>
      </div>

      {/* Credit Report Analyzer Section */}
      {showAnalyzer && (
        <CreditReportAnalyzer
          clientId={id}
          onScoreExtracted={(scores) => {
            // Update credit scores from extracted report
            if (scores.experian || scores.equifax || scores.transunion) {
              const avgScore = Math.round(
                [scores.experian, scores.equifax, scores.transunion]
                  .filter(s => s)
                  .reduce((a, b) => a + b, 0) / 
                [scores.experian, scores.equifax, scores.transunion].filter(s => s).length
              );
              setCreditScore(avgScore.toString());
              toast.success(`Credit score detected: ${avgScore}`);
            }
          }}
          onAccountsExtracted={(accounts) => {
            setExtractedAccounts(accounts);
            toast.success(`Extracted ${accounts.all.length} accounts for simulation`);
          }}
        />
      )}

      {/* Input Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Score Input */}
        <Card className="border-white/5 shadow-xl shadow-black/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Current Credit Score
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* 3 Bureau Scores Display */}
            {(client?.score_equifax || client?.score_experian || client?.score_transunion) ? (
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="p-3 bg-secondary/30 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">Equifax</p>
                  <p className={`text-xl font-bold ${getScoreColor(client?.score_equifax || 0)}`}>
                    {client?.score_equifax || '---'}
                  </p>
                </div>
                <div className="p-3 bg-secondary/30 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">Experian</p>
                  <p className={`text-xl font-bold ${getScoreColor(client?.score_experian || 0)}`}>
                    {client?.score_experian || '---'}
                  </p>
                </div>
                <div className="p-3 bg-secondary/30 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">TransUnion</p>
                  <p className={`text-xl font-bold ${getScoreColor(client?.score_transunion || 0)}`}>
                    {client?.score_transunion || '---'}
                  </p>
                </div>
              </div>
            ) : null}
            
            <div className="space-y-2">
              <Label>Average Score (for simulation)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="300"
                  max="850"
                  value={creditScore}
                  onChange={(e) => setCreditScore(e.target.value)}
                  placeholder="e.g., 580"
                  className="bg-secondary/50 border-white/10 text-2xl font-bold text-center"
                  data-testid="credit-score-input"
                />
                <Button 
                  onClick={saveScore} 
                  disabled={saving}
                  variant="outline"
                  className="border-white/10"
                  data-testid="save-score-btn"
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {client?.score_equifax ? "Calculated from your 3 bureau scores" : "Enter the score from Credit Karma, Experian, or any credit monitoring service"}
              </p>
            </div>
            
            {getCurrentScore() > 0 && (
              <div className="p-4 bg-primary/10 rounded-xl">
                <p className="text-sm text-muted-foreground">Current Average Score</p>
                <p className={`text-3xl font-bold ${getScoreColor(getCurrentScore())}`}>
                  {getCurrentScore()}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tradeline Input */}
        <Card className="border-white/5 shadow-xl shadow-black/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" />
              Authorized User Tradelines
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>How many tradelines to simulate adding?</Label>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setTradelines(Math.max(0, tradelines - 1))}
                  disabled={tradelines === 0}
                  className="border-white/10"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="text-4xl font-bold w-16 text-center">{tradelines}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setTradelines(Math.min(2, tradelines + 1))}
                  disabled={tradelines === 2}
                  className="border-white/10"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Good tradelines (5+ years, low utilization) can add +20-50 points each
              </p>
            </div>
            
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-400 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-400">What are AU Tradelines?</p>
                  <p className="text-muted-foreground mt-1">
                    Being added as an authorized user on someone's credit card with good history 
                    can boost your score by inheriting their positive payment history.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Run Simulation Button */}
      <Button
        onClick={runSimulation}
        disabled={simulating || (!client?.credit_score && !creditScore)}
        className="w-full py-6 text-lg glow-primary"
        data-testid="run-simulation-btn"
      >
        {simulating ? (
          <>
            <Calculator className="w-5 h-5 mr-2 animate-spin" />
            Calculating...
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5 mr-2" />
            Run Score Simulation
          </>
        )}
      </Button>

      {/* Simulation Results */}
      {simulation?.success && (
        <div className="space-y-6 animate-fade-in">
          {/* Score Journey */}
          <Card className="border-white/5 shadow-xl shadow-black/20 overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-primary/20 to-green-500/20">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Your Credit Score Journey
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Current */}
                <div className="text-center p-6 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <p className="text-sm text-muted-foreground mb-2">CURRENT</p>
                  <p className={`text-5xl font-bold ${getScoreColor(simulation.current_score)}`}>
                    {simulation.current_score}
                  </p>
                  <Badge className={`mt-2 ${getTierColor(simulation.current_tier)}`}>
                    {simulation.current_tier}
                  </Badge>
                </div>

                {/* After Repair */}
                <div className="text-center p-6 bg-blue-500/10 border border-blue-500/20 rounded-xl relative">
                  <div className="absolute -left-3 top-1/2 -translate-y-1/2 hidden md:block">
                    <ChevronRight className="w-6 h-6 text-blue-400" />
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">AFTER REPAIR</p>
                  <p className={`text-5xl font-bold ${getScoreColor(simulation.score_after_removal.projected)}`}>
                    {simulation.score_after_removal.projected}
                  </p>
                  <Badge className={`mt-2 ${getTierColor(simulation.score_after_removal.tier)}`}>
                    {simulation.score_after_removal.tier}
                  </Badge>
                  <p className="text-green-400 font-semibold mt-2">
                    +{simulation.score_after_removal.improvement.average} points
                  </p>
                </div>

                {/* Final with Tradelines */}
                <div className="text-center p-6 bg-green-500/10 border border-green-500/20 rounded-xl relative">
                  <div className="absolute -left-3 top-1/2 -translate-y-1/2 hidden md:block">
                    <ChevronRight className="w-6 h-6 text-green-400" />
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {tradelines > 0 ? `WITH ${tradelines} TRADELINE${tradelines > 1 ? 'S' : ''}` : 'FINAL PROJECTED'}
                  </p>
                  <p className={`text-5xl font-bold ${getScoreColor(simulation.final_projected_score.projected)}`}>
                    {simulation.final_projected_score.projected}
                  </p>
                  <Badge className={`mt-2 ${getTierColor(simulation.final_projected_score.tier)}`}>
                    {simulation.final_projected_score.tier}
                  </Badge>
                  <p className="text-green-400 font-semibold mt-2">
                    +{simulation.final_projected_score.total_improvement.average} total
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-8">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Progress to 850</span>
                  <span className="font-medium">
                    {simulation.current_score} → {simulation.final_projected_score.projected}
                  </span>
                </div>
                <div className="relative h-4 bg-secondary rounded-full overflow-hidden">
                  <div 
                    className="absolute inset-y-0 left-0 bg-red-500/50 transition-all"
                    style={{ width: `${((simulation.current_score - 300) / 550) * 100}%` }}
                  />
                  <div 
                    className="absolute inset-y-0 left-0 bg-green-500 transition-all"
                    style={{ width: `${((simulation.final_projected_score.projected - 300) / 550) * 100}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>300</span>
                  <span>580</span>
                  <span>670</span>
                  <span>740</span>
                  <span>850</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Derogatory Impact Breakdown */}
          <Card className="border-white/5 shadow-xl shadow-black/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-red-400" />
                Derogatory Accounts Impact ({simulation.derogatory_accounts.total_count} items)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {simulation.derogatory_accounts.breakdown.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No derogatory accounts found. Great job!
                </p>
              ) : (
                <div className="space-y-3">
                  {simulation.derogatory_accounts.breakdown.map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl"
                    >
                      <div>
                        <p className="font-medium">{item.creditor}</p>
                        <p className="text-sm text-muted-foreground capitalize">
                          {item.type.replace('_', ' ')} • ${(item.balance || 0).toLocaleString()}
                        </p>
                      </div>
                      <Badge className="bg-green-500/20 text-green-400">
                        {item.estimated_impact}
                      </Badge>
                    </div>
                  ))}
                  
                  <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl mt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Total Potential Boost from Removal</span>
                      <span className="text-xl font-bold text-green-400">
                        +{simulation.derogatory_accounts.total_potential_boost.min} to +{simulation.derogatory_accounts.total_potential_boost.max} points
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tradeline Impact */}
          {tradelines > 0 && (
            <Card className="border-white/5 shadow-xl shadow-black/20 border-amber-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-400">
                  <Zap className="w-5 h-5" />
                  Authorized User Tradeline Boost
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-amber-500/10 rounded-xl text-center">
                    <p className="text-sm text-muted-foreground">Tradelines Added</p>
                    <p className="text-3xl font-bold text-amber-400">{simulation.tradelines.count}</p>
                  </div>
                  <div className="p-4 bg-amber-500/10 rounded-xl text-center">
                    <p className="text-sm text-muted-foreground">Per Tradeline</p>
                    <p className="text-3xl font-bold text-amber-400">
                      +{simulation.tradelines.boost_per_tradeline.min}-{simulation.tradelines.boost_per_tradeline.max}
                    </p>
                  </div>
                  <div className="p-4 bg-amber-500/10 rounded-xl text-center">
                    <p className="text-sm text-muted-foreground">Total Boost</p>
                    <p className="text-3xl font-bold text-amber-400">
                      +{simulation.tradelines.total_boost.average}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary */}
          <Card className="border-green-500/30 shadow-xl shadow-black/20 bg-gradient-to-br from-green-500/10 to-transparent">
            <CardContent className="p-6">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-4">Projected Results Summary</h3>
                <div className="inline-flex items-center gap-4 text-4xl font-bold">
                  <span className={getScoreColor(simulation.current_score)}>{simulation.current_score}</span>
                  <ChevronRight className="w-8 h-8 text-muted-foreground" />
                  <span className={getScoreColor(simulation.final_projected_score.projected)}>
                    {simulation.final_projected_score.projected}
                  </span>
                </div>
                <p className="text-2xl font-bold text-green-400 mt-4">
                  {simulation.summary.total_potential_gain}
                </p>
                <p className="text-muted-foreground mt-2">
                  From {simulation.current_tier} to {simulation.final_projected_score.tier}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* No Score Message */}
      {simulation && !simulation.success && (
        <Card className="border-amber-500/30 shadow-xl shadow-black/20">
          <CardContent className="p-8 text-center">
            <CreditCard className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Credit Score Required</h3>
            <p className="text-muted-foreground">
              {simulation.message}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
